import { ReactElement, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Details,
  Field,
  Heading,
  Input,
  Label,
  Link,
  Paragraph,
  Skeleton,
  Tag,
  Textarea,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import type { ChecklistCard } from '../../../api/safetyRounds';
import { Routes } from '../../../enums/Routes';
import { useChecklistsQuery, useCreateChecklistMutation } from '../../../hooks/queries/safetyRounds/useChecklistQueries';
import { dayAndMonth } from '../../WorkEnvironmentSurveyDetail/surveyProcessView';
import { cardStatus } from './checklistView';
import layout from '../../HRDashboard/hmsPage.module.css';
import classes from './Checklists.module.css';

export const checklistPath = (id: number) => Routes.HRSafetyRoundChecklistFullPath.replace(':checklistId', String(id));

export const ChecklistsPage = (): ReactElement => {
  const { t } = useTranslation();
  const { data, isLoading, error } = useChecklistsQuery();
  const [adding, setAdding] = useState(false);
  const waiting = (data ?? []).reduce((sum, card) => sum + card.newSuggestions, 0);

  const content = () => {
    if (isLoading)
      return (
        <div aria-busy="true" className={layout.loading}>
          <span className="ds-sr-only">{t('safetyRounds.checklists.loading')}</span>
          <Skeleton variant="rectangle" width="100%" height={420} />
        </div>
      );
    if (error || !data)
      return (
        <div className={layout.loading}>
          <Alert data-color="danger">
            <Heading level={2} data-size="2xs">
              {t('safetyRounds.errors.loadTitle')}
            </Heading>
            <Paragraph>{t('safetyRounds.errors.loadMessage')}</Paragraph>
          </Alert>
        </div>
      );
    return (
      <div className={classes.content}>
        {waiting > 0 && (
          <Alert data-color="info" data-size="sm">
            <Paragraph data-size="sm">{t('safetyRounds.checklists.suggestionsWaiting', { count: waiting })}</Paragraph>
          </Alert>
        )}
        {adding && <NewChecklistForm onCancel={() => setAdding(false)} />}
        <ul className={classes.cards}>
          {data.map((card) => (
            <ChecklistCardItem key={card.id} card={card} />
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div className={layout.page} data-size="md">
      <section className={layout.hero}>
        <div aria-hidden="true" className={layout.backdrop} />
        <div className={`${layout.heroContent} ${classes.heroRow}`}>
          <div className={classes.heroText}>
            <span className={layout.eyebrow}>{t('safetyRounds.checklists.eyebrow')}</span>
            <Heading level={1} data-size="xl">
              {t('safetyRounds.checklists.title')}
            </Heading>
            <Paragraph className={layout.intro}>{t('safetyRounds.checklists.intro')}</Paragraph>
          </div>
          {!adding && (
            <Button data-size="sm" onClick={() => setAdding(true)}>
              {t('safetyRounds.checklists.new')}
            </Button>
          )}
        </div>
      </section>
      {content()}
    </div>
  );
};

const ChecklistCardItem = ({ card }: { card: ChecklistCard }): ReactElement => {
  const { t, i18n } = useTranslation();
  const status = cardStatus(card, t);
  return (
    <li className={classes.card}>
      <div className={classes.tags}>
        <Tag data-color={status.color} data-size="sm">
          {status.label}
        </Tag>
        {card.version !== null && (
          <Tag data-color="neutral" data-size="sm">
            {t('safetyRounds.checklists.version', { version: card.version })}
          </Tag>
        )}
      </div>
      <div>
        <Heading level={2} data-size="sm">
          {card.name}
        </Heading>
        <Paragraph data-size="sm" className={layout.subtle}>
          {card.categories.join(' · ')}
        </Paragraph>
      </div>
      <dl className={classes.facts}>
        <div>
          <dt>{t('safetyRounds.checklists.checkpoints')}</dt>
          <dd>{card.checkpoints}</dd>
        </div>
        <div>
          <dt>{t('safetyRounds.checklists.required')}</dt>
          <dd>{card.required}</dd>
        </div>
        <div>
          <dt>{t('safetyRounds.checklists.usedIn')}</dt>
          <dd>{t('safetyRounds.checklists.plans', { count: card.activePlans })}</dd>
        </div>
      </dl>
      {card.newSuggestions > 0 && (
        <Paragraph data-size="sm">{t('safetyRounds.checklists.cardSuggestions', { count: card.newSuggestions })}</Paragraph>
      )}
      {card.versions.length > 0 && (
        <Details data-size="sm">
          <Details.Summary>{t('safetyRounds.checklists.versions')}</Details.Summary>
          <Details.Content>
            <ul className={classes.versions}>
              {card.versions.map((version) => (
                <li key={version.version}>
                  <span className="ds-label" data-size="sm">
                    {t('safetyRounds.checklists.versionShort', { version: version.version })}
                  </span>{' '}
                  {[version.publishedAt && dayAndMonth(version.publishedAt, i18n.language), version.changeNote]
                    .filter(Boolean)
                    .join(' · ')}
                </li>
              ))}
            </ul>
          </Details.Content>
        </Details>
      )}
      <div>
        <Link asChild data-size="sm">
          <RouterLink to={checklistPath(card.id)}>
            {t('safetyRounds.checklists.edit', { name: card.name })}
          </RouterLink>
        </Link>
      </div>
    </li>
  );
};

const NewChecklistForm = ({ onCancel }: { onCancel: () => void }): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const create = useCreateChecklistMutation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tried, setTried] = useState(false);
  const missingName = !name.trim();

  const submit = () => {
    setTried(true);
    if (missingName) return;
    create.mutate(
      { name: name.trim(), description: description.trim() },
      { onSuccess: ({ id }) => navigate(checklistPath(id)) },
    );
  };

  return (
    <section aria-labelledby="new-checklist" className={layout.card} data-gap="md">
      <Heading level={2} data-size="sm" id="new-checklist">
        {t('safetyRounds.checklists.newTitle')}
      </Heading>
      <Field>
        <Label htmlFor="new-checklist-name">{t('safetyRounds.checklist.name')}</Label>
        <Input
          id="new-checklist-name"
          value={name}
          aria-invalid={tried && missingName ? true : undefined}
          onChange={(event) => setName(event.target.value)}
        />
        {tried && missingName && <ValidationMessage>{t('safetyRounds.checklist.errors.name')}</ValidationMessage>}
      </Field>
      <Field>
        <Label htmlFor="new-checklist-description">{t('safetyRounds.checklist.description')}</Label>
        <Textarea
          id="new-checklist-description"
          rows={2}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </Field>
      {create.isError && (
        <Alert data-color="danger" data-size="sm">
          <Paragraph data-size="sm">{t('hms.errors.save')}</Paragraph>
        </Alert>
      )}
      <div className={layout.actions}>
        <Button data-size="sm" onClick={submit} aria-busy={create.isPending || undefined}>
          {t('safetyRounds.checklists.create')}
        </Button>
        <Button data-size="sm" variant="tertiary" onClick={onCancel}>
          {t('safetyRounds.cancel')}
        </Button>
      </div>
    </section>
  );
};
